import { useMemo, useState } from "react";

interface KeyedLookupEntry {
  key: string;
  text: string;
}

interface PromptKeyedLookupSelectProps {
  /** Current value — a known key, a resolved text (from the registry), or a
   *  custom literal. Reverse-lookup by text→key runs internally so the
   *  dropdown shows the right selection even when the registry hands us
   *  resolved text rather than the yaml key. */
  value: string;
  onChange: (next: string) => void;
  items: KeyedLookupEntry[];
  isLoading?: boolean;
  disabled?: boolean;
  /** Show the "Other (custom)…" fallback option. Off for fields like
   *  templated_instructions where the pipelines resolver has no literal
   *  fallback — an unknown key rejects the PR outright, and (unlike
   *  role) there's no auto-registration path to fall back on, so this
   *  field only offers real catalog keys. */
  allowCustom?: boolean;
}

const CUSTOM_SENTINEL = "__custom__";

/**
 * Generic dropdown for prompt yaml fields that reference a keyed lookup
 * (role, templated_instructions, technical_instructions). All three share
 * the same shape — a top-level dict of short-name → full-text — and the
 * same UX: pick from the catalog, or (when ``allowCustom``) type a raw
 * literal.
 */
export const PromptKeyedLookupSelect: React.FC<PromptKeyedLookupSelectProps> = ({
  value,
  onChange,
  items,
  isLoading = false,
  disabled = false,
  allowCustom = false,
}) => {
  const knownKeys = useMemo(() => new Set(items.map((r) => r.key)), [items]);

  // The stored value could be:
  //   (a) A KEY (correct form-storage shape).
  //   (b) Resolved TEXT — because the registry stores resolved text for
  //       these fields, not the yaml key. Reverse-lookup text→key so the
  //       dropdown pre-selects correctly.
  //   (c) A truly custom string (only meaningful when allowCustom).
  const reverseLookup = useMemo(() => {
    if (!value) return null;
    if (knownKeys.has(value)) return value;
    const byText = items.find((r) => r.text === value);
    return byText ? byText.key : null;
  }, [value, items, knownKeys]);

  const [customMode, setCustomMode] = useState<boolean>(false);

  // A value is "catalog-known" when it's either a known key or
  // reverse-lookup-resolvable text.  The anti-stick guard on
  // ``effectiveCustomMode`` uses this to suppress a stale ``customMode``
  // flag when the parent form resets the value back to a catalog value
  // (e.g. "Undo changes"). But the guard MUST NOT fire when the user
  // has just explicitly clicked "Other (custom)…" — otherwise the
  // free-text input never appears (or disappears on their first
  // keystroke of catalog text). ``customMode`` here is exactly that
  // "user explicit intent" signal; keep it winning over the guard.
  const valueIsCatalogKnown = !!value && (knownKeys.has(value) || reverseLookup !== null);
  const effectiveCustomMode = allowCustom && (customMode || (!valueIsCatalogKnown && !!value));

  // Orphan = a non-empty stored value that doesn't map to any known key,
  // doesn't reverse-lookup by text, and can't fall back to custom mode
  // (allowCustom is off). Without special handling the native <select>
  // would silently render the first option while the parent still holds
  // the orphan — a UI that lies about state. Render the raw value as
  // its own (disabled) option and warn so the mismatch is visible.
  const isOrphan = !effectiveCustomMode && !!value && !knownKeys.has(value) && reverseLookup === null;

  const effectiveKey = reverseLookup ?? value;
  const selectValue = effectiveCustomMode ? CUSTOM_SENTINEL : effectiveKey;

  const resolvedText = useMemo(() => {
    if (effectiveCustomMode) return null;
    return items.find((r) => r.key === effectiveKey)?.text ?? null;
  }, [effectiveCustomMode, items, effectiveKey]);

  // While the catalog fetch is in flight, render a skeleton the same
  // height as the select so the dialog doesn't reflow when items land.
  // Also prevents the "empty dropdown then pop-in" glitch.
  if (isLoading) {
    return (
      <div className="flex flex-col gap-1">
        <div className="h-[38px] w-full rounded-md bg-limestone-200 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <select
        value={selectValue}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value;
          if (next === CUSTOM_SENTINEL) {
            // User explicitly picked "Other (custom)…". Set the
            // "user wants custom" flag and leave the value alone —
            // ``effectiveCustomMode`` now honors ``customMode`` even
            // when the value happens to be catalog-known, so the
            // free-text input renders pre-filled with the prior
            // selection (or empty, if there was none). User can
            // edit from there. Prevents saving ``role: null`` when
            // the user forgot to type after picking "Other".
            setCustomMode(true);
            onChange(value);
          } else {
            setCustomMode(false);
            onChange(next);
          }
        }}
        className="rounded-md border border-border-medium bg-bg-default px-3 py-2 text-sm text-text-primary"
      >
        <option value="">— None —</option>
        {items.map((r) => (
          <option key={r.key} value={r.key}>
            {r.key}
          </option>
        ))}
        {allowCustom && <option value={CUSTOM_SENTINEL}>Other (custom)…</option>}
        {isOrphan && (
          <option value={value} disabled>
            {value} (unknown — pick a valid option)
          </option>
        )}
      </select>
      {isOrphan && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          The saved value doesn&apos;t match any known catalog entry. Pick a valid option above to replace it.
        </div>
      )}
      {effectiveCustomMode && (
        // A textarea, not an input: an input's value drops line breaks, which
        // flattened multi-line custom instructions into one line on edit.
        <textarea
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Custom text"
          rows={Math.min(16, Math.max(4, value.split("\n").length))}
          className="resize-y rounded-md border border-border-medium bg-bg-default px-3 py-2 text-sm text-text-primary"
        />
      )}
      {resolvedText && (
        <div className="text-xs text-text-tertiary max-h-32 overflow-y-auto whitespace-pre-wrap rounded bg-limestone-50 border border-border-subtle px-2 py-1">
          {resolvedText}
        </div>
      )}
    </div>
  );
};
